import { useCallback, useRef, useState } from 'react'
import { NO_METRICS, scrollIntent, type ListMetrics } from '../dynamicSelectUtils'

type AppliedFiltersCollapse = {
  /** Whether the list has taken the applied filters' space. */
  hidden: boolean
  /** Feed in the metrics of the list on every scroll event. */
  noteScroll: (metrics: ListMetrics) => void
  /** Show them again and forget where the list was, for a list replaced wholesale. */
  reset: () => void
}

/**
 * Pins a filter's selections above its list until the user scrolls into it, then
 * brings them back on the first upward movement. Shared by the filter popups
 * that show their selections above a scrolling list, so that they all give way
 * at the same point in a drag and come back just as readily.
 */
export const useAppliedFiltersCollapse = (): AppliedFiltersCollapse => {
  const [hidden, setHidden] = useState<boolean>(false)
  const previousMetrics = useRef<ListMetrics>(NO_METRICS)

  const noteScroll = useCallback((metrics: ListMetrics) => {
    const intent = scrollIntent(metrics, previousMetrics.current)
    previousMetrics.current = metrics

    // A drag reports its intent on every frame, but only the first frame of it
    // changes anything. Writing the state regardless costs a render of the whole
    // list per frame, which is the difference between a smooth scroll and a
    // stuttering one once a few pages have loaded — so hand back the very same
    // value unless this is a real transition, and let React bail out.
    if (intent === 'reveal') setHidden(wasHidden => (wasHidden ? false : wasHidden))
    else if (intent === 'hide') setHidden(wasHidden => (wasHidden ? wasHidden : true))
  }, [])

  const reset = useCallback(() => {
    previousMetrics.current = NO_METRICS
    setHidden(false)
  }, [])

  return { hidden, noteScroll, reset }
}
